import { faHandPointUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import NumberOfAssetPositionsDisplayWidget from "components/Common/NumberOfAssetPositionsDisplayWidget";
import NewTextFormComponent from "components/Forms/NewTextFormComponent";
import PrivacyMode from "components/Text/PrivacyMode";
import {
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/Theme/table";
import UncontrolledTooltip from "components/Theme/tooltip";
import CompanyNavLink from "components/navigation/CompanyNavLink";
import DisplayIfUserType from "components/utils/DisplayIfUserType/DisplayIfUserType";
import { Form, Formik } from "formik";
import {
  type GetSchemeListSchemeGetQueryParams,
  type Scheme,
  useGetSchemeListSchemeGet,
} from "kubb";
import type React from "react";
import { useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import NewGetListPaginatedTable from "../../../components/Pagination/GetListPaginatedTable";
import {
  convertStringUndefined,
  createSearchString,
} from "../../../components/navigation/SearchString";
import { getUrlHash } from "../../../components/navigation/URL";
import { useSelectedCompany } from "../../../hooks/useCompany";
import { useSelectedScheme } from "../../../hooks/useScheme";
import { usePrivacyMode } from "../../../reducers/privacy";

// Keep in sync with the server-side Field(max_length=200) on
// GetSchemeListCommand.text_search — enforced at the input so users can't
// type past the cap and trip backend validation.
const TEXT_SEARCH_MAX_LENGTH = 200;

// `SchemesList` is always rendered via `SchemeListSearchParam`, which uses
// `React.cloneElement` to inject `{ params, initialized }`. `params` is
// optional on the TS side because tests may render the component directly
// without the parser; at runtime under `SchemeListSearchParam` it's always
// populated (possibly to `{}`) before `initialized` flips to true.
interface SchemesListProps {
  params?: {
    text_search?: string;
    max_items?: number;
  };
}

const HEADER_ROW = (
  <TableHead>
    <TableRow>
      <TableHeader>ID</TableHeader>
      <TableHeader>Name</TableHeader>
      <TableHeader>Location</TableHeader>
      <TableHeader>No. of Asset Positions</TableHeader>
      <TableHeader>No. of Locations</TableHeader>
      <TableHeader>&nbsp;</TableHeader>
    </TableRow>
  </TableHead>
);

const SchemesList = (props: SchemesListProps): React.ReactElement => {
  const textSearch = props.params?.text_search;
  const maxItems = props.params?.max_items;
  const company = useSelectedCompany();
  const privacy = usePrivacyMode();
  const selected = useSelectedScheme();
  const navigate = useNavigate();
  const location = useLocation();

  const setFiltersToRoute = useCallback(
    (values: { text_search?: string }) => {
      const newSearchString = createSearchString(
        convertStringUndefined({
          text_search: values.text_search || undefined,
        }),
      );
      const want = newSearchString ? `?${newSearchString}` : "";
      if (location.search !== want) {
        navigate({ ...location, search: newSearchString });
      }
    },
    [navigate, location],
  );

  const row = (
    value: Scheme,
    _update: (updatedItem: Scheme) => void,
  ): React.ReactElement<{ key: string }> => {
    const isSelected =
      value.scheme_id === selected?.scheme_id &&
      value.company_id === selected.company_id;
    return (
      <TableRow
        key={value.scheme_id}
        data-cy="schemes-list-item"
        className={clsx({
          "bg-brand-light-200": isSelected,
        })}
      >
        <TableCell data-cy="scheme-name">
          <PrivacyMode>{value.scheme_id}</PrivacyMode>
        </TableCell>
        <TableCell data-cy="scheme-name">
          <PrivacyMode>{value.scheme_name}</PrivacyMode>
        </TableCell>
        <TableCell data-cy="scheme-location">
          <PrivacyMode>{value.location}</PrivacyMode>
        </TableCell>
        <TableCell data-cy="scheme-devices">
          <NumberOfAssetPositionsDisplayWidget
            positions={value.number_of_asset_positions}
            index={0}
          />
        </TableCell>
        <TableCell data-cy="scheme-locations">
          {value.number_of_locations}
        </TableCell>
        <TableCell className="table-actions">
          {!isSelected && (
            <UncontrolledTooltip
              message={
                <>
                  Select <PrivacyMode>{value.scheme_name}</PrivacyMode>
                </>
              }
            >
              <NavLink
                className="table-action table-action-delete"
                to={{ hash: getUrlHash(company, value, privacy) }}
                id={`tooltip-select-company-${value.scheme_id}`}
              >
                <FontAwesomeIcon icon={faHandPointUp} />
              </NavLink>
            </UncontrolledTooltip>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const params: Omit<GetSchemeListSchemeGetQueryParams, "company_id"> & {
    company_id: string | undefined;
  } = {
    company_id: company?.company_id,
    text_search: textSearch,
  };

  return (
    <>
      <Formik
        initialValues={{ text_search: textSearch ?? "" }}
        enableReinitialize
        onSubmit={(values) => setFiltersToRoute(values)}
      >
        <Form>
          <NewTextFormComponent
            fieldName="text_search"
            label={{ label: "Search", size: "sm" }}
            placeholder="Filter by scheme name and press Enter"
            dataCy="scheme-list-search"
            maxLength={TEXT_SEARCH_MAX_LENGTH}
          />
        </Form>
      </Formik>
      <NewGetListPaginatedTable<Scheme, typeof params>
        params={params}
        cursor={{ max_items: maxItems ?? 25 }}
        data-cy="scheme-list"
        header={[HEADER_ROW, 6]}
        row={row}
        useQuery={useGetSchemeListSchemeGet}
        additionalFunctionButtons={
          <DisplayIfUserType userTypes={"admin"}>
            <CompanyNavLink
              button={{
                color: "brandLight",
              }}
              to={{ pathname: "/admin/schemes/create" }}
            >
              Create New
            </CompanyNavLink>
          </DisplayIfUserType>
        }
      />
    </>
  );
};

export default SchemesList;
